import {
  Datagrid,
  DateField,
  DateTimeInput,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import type { Event } from '../dataProvider';
import { EVENT_TYPE_CHOICES, eventsExporter } from './eventsModel';

/**
 * The events explorer list (D-events-explorer, Phase 9), a READ-ONLY debug / search tool
 * over the events stream for support and engineering. It REUSES the Phase 7 AuditList
 * search-filter skeleton (a free-text q, SelectInput / TextInput facets, a From / To
 * DateTimeInput range, occurredAt DESC, read-only datagrid) and adds a CSV export (the
 * first in the panel). The detail (the full JSONB property bag) is the EventShow view,
 * reached by a row click.
 *
 * Region-scoped by the SWITCHER (filter region, the materialized market), so a uk admin
 * searches uk events. The free-text q substring-matches the serialized properties bag (the
 * materialized propertiesText), so a store_id typed into q finds the events carrying it.
 * ROLE-gated to support, super_admin and fraud (the debug audience): the surface exposes
 * member behavioural traces, so a role gate holds the boundary against a viewer's
 * read-everywhere '*:read' (the AuditList precedent).
 */

/** A compact one-line preview of the properties bag for the list row. */
const PropertiesPreview = ({ record }: { record: Event }) => {
  const text = JSON.stringify(record.properties ?? {});
  const shown = text.length > 60 ? `${text.slice(0, 59)}…` : text;
  return (
    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
      {shown}
    </span>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const EventListView = () => {
  const { region } = useRegion();
  const eventFilters = [
    <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
    <SelectInput
      key="eventType"
      source="eventType"
      label="Event type"
      choices={EVENT_TYPE_CHOICES}
    />,
    <TextInput key="userId" source="userId" label="User id" />,
    <DateTimeInput key="from" source="occurredAt_gte" label="From" />,
    <DateTimeInput key="to" source="occurredAt_lte" label="To" />,
  ];

  return (
    <List
      resource="events"
      title="Events explorer"
      filters={eventFilters}
      // Scope, not activeRegion: the switcher scopes the corpus (marketsForScope).
      filter={{ region }}
      sort={{ field: 'occurredAt', order: 'DESC' }}
      perPage={25}
      exporter={eventsExporter}
    >
      {/* Read-only: no bulk actions (no mutation). Row click opens the JSONB detail. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <DateField source="occurredAt" label="When" showTime />
        <TextField source="userId" label="User" />
        <TextField source="eventType" label="Type" />
        <FunctionField
          label="Market"
          render={(r: Event) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Properties"
          render={(r: Event) => <PropertiesPreview record={r} />}
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the events Resource's list. */
export const EventList = () => (
  <RequireAccess roles={['support', 'super_admin', 'fraud']}>
    <EventListView />
  </RequireAccess>
);
